/**
 * AdminChatFeedbackTab.jsx
 *
 * Reviews chat conversations that need attention:
 *   • 👎 messages (user explicitly disliked)
 *   • Unmatched questions (`faqs_used = []` — no FAQ ground for the
 *     reply, the bot fell back to general GPT knowledge)
 *
 * Each row can be "Promoted to FAQ" — the user's verbatim question
 * becomes a new FAQ entry with an admin-supplied canonical answer.
 * Closes the learning loop: real user questions → permanent knowledge.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { toast } from "sonner";
import {
  ThumbsDown, ThumbsUp, Search, RefreshCw, BookPlus, X, MessageSquare,
  Sparkles, Layers,
} from "lucide-react";

const TOKEN_KEY = "trp_admin_token";
const headers = () => ({ "X-Admin-Token": localStorage.getItem(TOKEN_KEY) || "" });

const LANGS = ["sk", "en", "cs", "de", "pl", "hu", "fr", "it", "es", "pt"];

export default function AdminChatFeedbackTab() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [filter, setFilter] = useState({ rating: "down", lang: "", days: 30 });
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [promoting, setPromoting] = useState(null); // {row, scope, answer, weight}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: filter.days };
      if (filter.rating) params.rating = filter.rating;
      if (filter.lang) params.lang = filter.lang;
      const r = await axios.get(`${API}/admin/chat-feedback`, { headers: headers(), params });
      setStats(r.data.stats);
      setRows(r.data.rows || []);
      setTokenError(false);
    } catch (e) {
      if (e?.response?.status === 403) setTokenError(true);
      else toast.error("Načítanie zlyhalo");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const saveToken = () => {
    localStorage.setItem(TOKEN_KEY, token.trim());
    setTokenError(false);
    load();
  };

  const startPromote = (row) => {
    setPromoting({
      row, scope: "global", weight: 2,
      // Prefill the answer with the bot's SK reply — admin polishes
      // before saving. Reduces busywork on common, well-answered Qs.
      answer: row.answer_sk || row.bot_response || "",
    });
  };
  const cancelPromote = () => setPromoting(null);

  const doPromote = async () => {
    if (!promoting?.answer?.trim()) {
      toast.error("Doplň odpoveď"); return;
    }
    try {
      const r = await axios.post(
        `${API}/admin/chat-feedback/${promoting.row.message_id}/promote`,
        {
          scope: promoting.scope || "global",
          answer: promoting.answer,
          weight: promoting.weight || 2,
        },
        { headers: headers() },
      );
      toast.success(`FAQ vytvorená (${r.data.faq.id})`);
      setPromoting(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "already_promoted") {
        toast.error(`Táto otázka už bola promotnutá ako ${detail.faq_id}.`);
        setPromoting(null);
        load();
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Promote zlyhal");
    }
  };

  if (tokenError || !token) {
    return (
      <div data-testid="admin-chat-feedback-panel" className="max-w-md mx-auto p-6 bg-white border border-amber-300 rounded-lg">
        <h2 className="text-lg font-bold mb-2 flex items-center gap-2 text-gray-900">
          <MessageSquare className="w-5 h-5" /> Chat Feedback Review
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          Zadaj <code className="bg-gray-100 px-1">ADMIN_TOKEN</code> pre prístup.
        </p>
        <input
          type="password" value={token}
          onChange={(e) => setToken(e.target.value)}
          data-testid="admin-chat-feedback-token-input"
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 mb-2"
          placeholder="Admin token"
        />
        <button
          onClick={saveToken}
          data-testid="admin-chat-feedback-token-save"
          className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-bold"
        >Uložiť a načítať</button>
      </div>
    );
  }

  return (
    <div data-testid="admin-chat-feedback-panel" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900">
          <MessageSquare className="w-5 h-5" /> Chat Feedback Review
        </h2>
        <button
          onClick={load}
          data-testid="admin-chat-feedback-refresh"
          className="px-3 py-2 border border-gray-300 hover:bg-gray-50 text-xs font-bold uppercase tracking-widest flex items-center gap-1 text-gray-700"
        ><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>

      <FaqSuggestionsPanel />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-chat-feedback-stats">
          <Kpi label="Správ" value={stats.total_messages} sub={`za ${stats.window_days} dní`} />
          <Kpi label="👍 počet" value={stats.thumbs_up} color="text-emerald-600" />
          <Kpi label="👎 počet" value={stats.thumbs_down} color="text-rose-600" />
          <Kpi label="Bez FAQ matchu" value={stats.unmatched} color="text-amber-600" sub="needs FAQ" />
          <Kpi label="FAQ coverage" value={`${stats.coverage_pct}%`} color={stats.coverage_pct >= 70 ? "text-emerald-600" : "text-amber-600"} />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap bg-gray-50 p-3 rounded">
        <Search className="w-4 h-4 text-gray-500" />
        <select
          value={filter.rating}
          onChange={(e) => setFilter({ ...filter, rating: e.target.value })}
          data-testid="admin-chat-feedback-filter-rating"
          className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
        >
          <option value="down">👎 Iba dislajknuté</option>
          <option value="up">👍 Iba lajknuté</option>
          <option value="any">Akýkoľvek feedback (👍/👎)</option>
          <option value="unmatched">Bez FAQ matchu (priority)</option>
          <option value="">Všetky správy</option>
        </select>
        <select
          value={filter.lang}
          onChange={(e) => setFilter({ ...filter, lang: e.target.value })}
          data-testid="admin-chat-feedback-filter-lang"
          className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
        >
          <option value="">Všetky jazyky</option>
          {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
        <select
          value={filter.days}
          onChange={(e) => setFilter({ ...filter, days: parseInt(e.target.value) })}
          data-testid="admin-chat-feedback-filter-days"
          className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
        >
          <option value={7}>7 dní</option>
          <option value={30}>30 dní</option>
          <option value={90}>90 dní</option>
          <option value={365}>1 rok</option>
        </select>
      </div>

      {loading && <div className="text-sm text-gray-500">Načítavam…</div>}

      <div className="space-y-3">
        {rows.map((r) => (
          <FeedbackRow key={r.message_id} row={r} onPromote={startPromote} />
        ))}
        {!loading && rows.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            Žiadne správy v tomto okne. Skús zmeniť filter.
          </div>
        )}
      </div>

      {promoting && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="admin-chat-feedback-promote-modal"
          onClick={cancelPromote}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <BookPlus className="w-5 h-5" /> Vytvoriť FAQ z tejto otázky
              </h3>
              <button onClick={cancelPromote} data-testid="admin-chat-feedback-promote-close">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-3 bg-gray-50 rounded mb-3 text-sm">
              <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Otázka užívateľa</div>
              <div className="text-gray-900">{promoting.row.user_message || promoting.row.question_sk}</div>
              <div className="text-[10px] text-gray-400 mt-1">
                Jazyk: <span className="font-mono">{promoting.row.original_language || "sk"}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Scope (kategória)</label>
                <input
                  type="text" value={promoting.scope}
                  onChange={(e) => setPromoting({ ...promoting, scope: e.target.value })}
                  data-testid="admin-chat-feedback-promote-scope"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
                  placeholder="napr. pricing, cart_recovery, global"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Váha (1-10, default 2)</label>
                <input
                  type="number" min={1} max={10} value={promoting.weight}
                  onChange={(e) => setPromoting({ ...promoting, weight: parseInt(e.target.value) || 2 })}
                  data-testid="admin-chat-feedback-promote-weight"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
                />
              </div>
            </div>

            <label className="text-xs text-gray-600 block mb-1">
              Kanonická odpoveď (pošli na produkčný FAQ — uprav AI odpoveď ak treba)
            </label>
            <textarea
              rows={8} value={promoting.answer}
              onChange={(e) => setPromoting({ ...promoting, answer: e.target.value })}
              data-testid="admin-chat-feedback-promote-answer"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 mb-3"
              maxLength={2000}
            />

            <div className="flex justify-end gap-2">
              <button onClick={cancelPromote}
                data-testid="admin-chat-feedback-promote-cancel"
                className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">Zrušiť</button>
              <button onClick={doPromote}
                data-testid="admin-chat-feedback-promote-save"
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-bold flex items-center gap-1 hover:bg-blue-700">
                <BookPlus className="w-4 h-4" /> Vytvoriť FAQ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, color = "text-gray-900" }) {
  return (
    <div className="p-3 bg-white border border-gray-200 rounded">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`text-2xl font-black tabular-nums ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  );
}

function FeedbackRow({ row, onPromote }) {
  const rating = row.feedback_rating;
  const promoted = !!row.promoted_to_faq_id;
  const unmatched = !row.faqs_used || row.faqs_used.length === 0;
  return (
    <div
      data-testid={`admin-chat-feedback-row-${row.message_id}`}
      className="p-4 border border-gray-200 rounded bg-white"
    >
      <div className="flex items-start gap-3 mb-2">
        <div className="flex flex-col gap-1 min-w-[80px]">
          {rating === "up" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-widest rounded">
              <ThumbsUp className="w-3 h-3" /> Up
            </span>
          )}
          {rating === "down" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-50 text-rose-700 text-[10px] font-bold uppercase tracking-widest rounded">
              <ThumbsDown className="w-3 h-3" /> Down
            </span>
          )}
          {unmatched && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-widest rounded">
              No FAQ
            </span>
          )}
          {promoted && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-widest rounded">
              Promoted
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-gray-500 mb-1">
            {row.original_language?.toUpperCase()} · {new Date(row.timestamp).toLocaleString("sk-SK")}
            {row.faqs_used?.length > 0 && (
              <span className="ml-2 text-blue-600">· {row.faqs_used.length} FAQ used</span>
            )}
          </div>
          <p className="text-sm font-bold text-gray-900 mb-1">
            ❓ {row.user_message || row.question_sk}
          </p>
          <p className="text-sm text-gray-600 line-clamp-3">
            🤖 {row.bot_response || row.answer_sk}
          </p>
          {row.feedback_note && (
            <p className="text-xs italic text-gray-500 mt-1">„{row.feedback_note}"</p>
          )}
        </div>
        {!promoted && (
          <button
            onClick={() => onPromote(row)}
            data-testid={`admin-chat-feedback-promote-${row.message_id}`}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold uppercase tracking-widest flex items-center gap-1 rounded whitespace-nowrap"
          >
            <BookPlus className="w-3 h-3" /> Promote → FAQ
          </button>
        )}
      </div>
    </div>
  );
}



/**
 * FaqSuggestionsPanel — auto-clusters unmatched questions from chat_logs
 * and surfaces them as suggested FAQ batches. Sub-section inside the
 * Chat Feedback tab — separate panel because the data shape (clusters,
 * not individual rows) is different from the message-level review above.
 */
function FaqSuggestionsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(14);
  const [creating, setCreating] = useState(null); // {cluster, scope, answer, weight, lang}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/faq-suggestions`, {
        headers: headers(),
        params: { days, min_cluster_size: 1, limit: 20 },
      });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error("Suggestions load failed");
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const openCreate = (cluster) => {
    setCreating({
      cluster,
      scope: "global",
      lang: cluster.languages?.[0] || "sk",
      weight: 2,
      answer: cluster.suggested_answer_seed || "",
    });
  };

  const submitCreate = async () => {
    if (!creating?.answer?.trim()) {
      toast.error("Doplň odpoveď"); return;
    }
    try {
      await axios.post(`${API}/admin/faqs`, {
        scope: creating.scope || "global",
        lang: creating.lang || "sk",
        question: creating.cluster.representative_question,
        answer: creating.answer,
        weight: creating.weight || 2,
        keywords: creating.cluster.keywords || [],
      }, { headers: headers() });
      toast.success("FAQ vytvorená zo suggested batchu");
      setCreating(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vytvorenie zlyhalo");
    }
  };

  return (
    <div className="p-4 bg-gradient-to-br from-amber-50 to-white border-2 border-amber-300 rounded-lg" data-testid="faq-suggestions-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold flex items-center gap-2 text-gray-900">
          <Sparkles className="w-4 h-4 text-amber-600" />
          Suggested FAQs · auto-extracted z unmatched otázok
          {data && (
            <span className="text-xs font-normal text-gray-500">
              ({data.total_unmatched} unmatched · {data.cluster_count} clusters)
            </span>
          )}
        </h3>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          data-testid="faq-suggestions-days"
          className="px-2 py-1 border border-gray-300 rounded text-xs text-gray-900"
        >
          <option value={7}>7 dní</option>
          <option value={14}>14 dní</option>
          <option value={30}>30 dní</option>
          <option value={90}>90 dní</option>
        </select>
      </div>

      {loading && <div className="text-xs text-gray-500">Načítavam clusters…</div>}

      {!loading && (!data || data.clusters?.length === 0) && (
        <div className="text-xs text-gray-500 italic">
          Žiadne unmatched otázky v tomto window-i. Chat je dobre pokrytý FAQ-kami 👏
        </div>
      )}

      <div className="space-y-2">
        {(data?.clusters || []).map((c, i) => (
          <div
            key={i}
            data-testid={`faq-suggestion-cluster-${i}`}
            className="p-3 bg-white border border-amber-200 rounded flex gap-3 items-start"
          >
            <div className="flex flex-col items-center min-w-[50px]">
              <span className="text-2xl font-black text-amber-600 tabular-nums">{c.size}×</span>
              <span className="text-[10px] uppercase tracking-widest text-gray-400">opýtaní</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 mb-1">{c.representative_question}</p>
              <div className="flex flex-wrap gap-1 mb-1">
                {(c.keywords || []).slice(0, 6).map((k) => (
                  <span key={k} className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">{k}</span>
                ))}
              </div>
              <p className="text-[11px] text-gray-500">
                <Layers className="w-3 h-3 inline mr-1" />
                {c.languages.join(", ").toUpperCase()}
              </p>
            </div>
            <button
              onClick={() => openCreate(c)}
              data-testid={`faq-suggestion-create-${i}`}
              className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold uppercase tracking-widest flex items-center gap-1 rounded whitespace-nowrap"
            >
              <BookPlus className="w-3 h-3" /> Create FAQ
            </button>
          </div>
        ))}
      </div>

      {creating && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="faq-suggestion-modal"
          onClick={() => setCreating(null)}
        >
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <BookPlus className="w-5 h-5" /> FAQ z cluster-u ({creating.cluster.size}× opýtaní)
              </h3>
              <button onClick={() => setCreating(null)} data-testid="faq-suggestion-modal-close">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-3 bg-gray-50 rounded mb-3 text-sm">
              <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Reprezentatívna otázka</div>
              <div className="text-gray-900">{creating.cluster.representative_question}</div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Scope</label>
                <input
                  type="text" value={creating.scope}
                  onChange={(e) => setCreating({ ...creating, scope: e.target.value })}
                  data-testid="faq-suggestion-modal-scope"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Jazyk</label>
                <select
                  value={creating.lang}
                  onChange={(e) => setCreating({ ...creating, lang: e.target.value })}
                  data-testid="faq-suggestion-modal-lang"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
                >
                  {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Váha</label>
                <input
                  type="number" min={1} max={10} value={creating.weight}
                  onChange={(e) => setCreating({ ...creating, weight: parseInt(e.target.value) || 2 })}
                  data-testid="faq-suggestion-modal-weight"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
                />
              </div>
            </div>

            <label className="text-xs text-gray-600 block mb-1">Kanonická odpoveď</label>
            <textarea
              rows={6} value={creating.answer}
              onChange={(e) => setCreating({ ...creating, answer: e.target.value })}
              data-testid="faq-suggestion-modal-answer"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 mb-3"
              maxLength={2000}
            />

            <div className="flex justify-end gap-2">
              <button onClick={() => setCreating(null)} className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">Zrušiť</button>
              <button onClick={submitCreate}
                data-testid="faq-suggestion-modal-save"
                className="px-4 py-2 bg-amber-500 text-white rounded text-sm font-bold flex items-center gap-1 hover:bg-amber-600">
                <BookPlus className="w-4 h-4" /> Vytvoriť FAQ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
